import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import {
  ApiService,
  BillingPlansResponse,
  BillingProfileResponse,
  CurrentUserProfile,
  InitializeSubscriptionSetupResponse,
  TopupPackOption,
} from '../../shared/services/api.service';

type IndividualPlanCard = BillingPlansResponse['plans'][number] & {
  description: string;
  displayMonthlyPriceCents: number;
  cardRequired: boolean;
  highlights: string[];
};

type SelectorStep = 'plans' | 'packs';

type PaystackInlineTransaction = {
  reference?: string;
  trxref?: string;
};

type PaystackInlineClient = {
  resumeTransaction: (
    accessCode: string,
    callbacks?: {
      onLoad?: (response: { accessCode?: string }) => void;
      onSuccess?: (transaction: PaystackInlineTransaction) => void;
      onCancel?: () => void;
      onError?: (error: { message?: string }) => void;
    }
  ) => void;
};

type PaystackInlineConstructor = new () => PaystackInlineClient;

@Component({
  selector: 'app-billing-plan-selector',
  templateUrl: './billing-plan-selector.component.html',
  styleUrls: ['./billing-plan-selector.component.css'],
})
export class BillingPlanSelectorComponent implements OnInit {
  private readonly supportEmail = 'admin@thebigdatashowroom.com';
  loading = true;
  awaitingPaymentConfirmation = false;
  awaitingPaymentMessage = '';
  pendingConfirmationTimedOut = false;
  activePendingReference: string | null = null;
  currentUserId: string | null = null;
  loadingMessage = 'Loading available plans...';
  selectingPlanCode: string | null = null;
  plans: IndividualPlanCard[] = [];
  selectorScope: 'individual' | 'team' = 'individual';
  step: SelectorStep = 'plans';
  topupPackOptions: TopupPackOption[] = [];
  selectedTopupPack = 100;
  purchasingTopup = false;
  private readonly maxVerifyPollAttempts = 20;
  private readonly verifyPollIntervalMs = 2500;

  constructor(
    private readonly apiService: ApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadCurrentUserContext();
    this.selectorScope = this.resolveScopeFromRoute();
    const callbackReference = String(this.route.snapshot.queryParamMap.get('paystackReference') || '').trim();
    const topupReference = String(this.route.snapshot.queryParamMap.get('topupReference') || '').trim();
    const stepParam = String(this.route.snapshot.queryParamMap.get('step') || '').trim().toLowerCase();
    if (callbackReference) {
      this.beginAwaitingPaymentConfirmation(callbackReference, 'subscription');
      return;
    }
    if (topupReference) {
      this.beginAwaitingPaymentConfirmation(topupReference, 'topup');
      return;
    }
    if (stepParam === 'packs' && this.selectorScope === 'individual') {
      this.step = 'packs';
      this.loadingMessage = 'Loading credit packs...';
      this.loadPackStep();
      return;
    }
    this.loadingMessage = 'Loading available plans...';
    this.loadSelectorData();
  }

  onChoosePlan(plan: IndividualPlanCard): void {
    if (this.selectingPlanCode) {
      return;
    }
    this.selectingPlanCode = plan.planCode;

    if (plan.planCode === 'INDIVIDUAL_PAYG') {
      this.apiService.selectBillingPlan(plan.planCode).subscribe({
        next: () => {
          this.selectingPlanCode = null;
          this.step = 'packs';
          void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { step: 'packs' },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
          this.loadPackStep();
        },
        error: (error) => {
          this.snackBar.open(error?.error?.error?.message || 'Failed to select plan', 'Close', {
            duration: 5000,
          });
          this.selectingPlanCode = null;
        },
      });
      return;
    }

    this.apiService.initializeSubscriptionSetup({
      planCode: plan.planCode,
      scope: this.selectorScope,
    }).subscribe({
      next: (init) => {
        const resumableAccessCode = String(init.accessCode || '').trim();
        if (init.pendingExisting === true && !resumableAccessCode) {
          this.snackBar.open(
            'A payment for this plan is already pending confirmation. We will keep monitoring that transaction.',
            'Close',
            { duration: 6000 }
          );
        }
        void this.openPaystackCheckout(init);
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to initialize payment', 'Close', {
          duration: 5000,
        });
        this.selectingPlanCode = null;
      },
    });
  }

  onSelectedPackChange(packSize: number): void {
    this.selectedTopupPack = packSize;
  }

  onBuyPackCredits(packSize: number): void {
    if (this.purchasingTopup) return;
    this.purchasingTopup = true;
    this.apiService.purchaseTopup(packSize, { returnPath: '/billing/plan-selector' }).subscribe({
      next: (init) => {
        void this.openTopupPaystackCheckout(init);
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to start top-up purchase', 'Close', {
          duration: 5000,
        });
        this.purchasingTopup = false;
      },
    });
  }

  onSkipPacks(): void {
    void this.navigateToDefaultAfterSetup();
  }

  private async openPaystackCheckout(init: InitializeSubscriptionSetupResponse): Promise<void> {
    const resumableAccessCode = String(init.accessCode || '').trim();
    if (init.alreadySettled === true) {
      const reference = String(init.reference || '').trim();
      if (!reference) {
        this.snackBar.open('Billing setup completed but confirmation reference is missing', 'Close', { duration: 5000 });
        return;
      }
      this.beginAwaitingPaymentConfirmation(reference, 'subscription');
      return;
    }
    if (init.pendingExisting === true && !resumableAccessCode) {
      const reference = String(init.reference || '').trim();
      if (!reference) {
        this.snackBar.open('Pending payment reference is missing', 'Close', { duration: 5000 });
        return;
      }
      this.beginAwaitingPaymentConfirmation(reference, 'subscription');
      return;
    }
    if (init.setupBypassed === true) {
      const reference = String(init.reference || '').trim();
      if (!reference) {
        this.snackBar.open('Billing setup completed but confirmation reference is missing', 'Close', { duration: 5000 });
        return;
      }
      this.beginAwaitingPaymentConfirmation(reference, 'subscription');
      return;
    }
    const accessCode = String(init.accessCode || '').trim();
    const reference = String(init.reference || '').trim();
    if (!accessCode) {
      this.snackBar.open('Missing Paystack access code from payment init', 'Close', { duration: 5000 });
      this.selectingPlanCode = null;
      return;
    }

    const inlineLoaded = await this.ensurePaystackInlineLoaded();
    const PaystackCtor = this.getPaystackInlineConstructor();
    if (!inlineLoaded || !PaystackCtor) {
      console.warn('[Paystack] Inline v2 unavailable, using redirect', { inlineLoaded, hasCtor: Boolean(PaystackCtor) });
      window.location.assign(init.authorizationUrl);
      return;
    }

    const popup = new PaystackCtor();
    if (typeof popup.resumeTransaction !== 'function') {
      console.warn('[Paystack] resumeTransaction missing on Inline v2 client, using redirect');
      window.location.assign(init.authorizationUrl);
      return;
    }

    try {
      popup.resumeTransaction(accessCode, {
        onSuccess: (transaction) => {
          const confirmedReference = String(transaction?.reference || transaction?.trxref || reference).trim();
          if (!confirmedReference) {
            this.snackBar.open('Payment confirmed but reference is missing', 'Close', { duration: 5000 });
            this.selectingPlanCode = null;
            return;
          }
          this.beginAwaitingPaymentConfirmation(confirmedReference, 'subscription');
        },
        onCancel: () => {
          this.snackBar.open('Payment setup was cancelled.', 'Close', { duration: 4000 });
          this.selectingPlanCode = null;
        },
        onError: (error) => {
          const message = String(error?.message || 'Paystack modal failed to load').trim();
          console.warn('[Paystack] Modal onError', error);
          this.snackBar.open(message, 'Close', { duration: 6000 });
          this.selectingPlanCode = null;
        },
      });
    } catch (error) {
      console.warn('[Paystack] Modal open failed, using redirect', error);
      window.location.assign(init.authorizationUrl);
    }
  }

  private async openTopupPaystackCheckout(init: {
    reference: string;
    accessCode: string;
    authorizationUrl: string;
  }): Promise<void> {
    const accessCode = String(init.accessCode || '').trim();
    const reference = String(init.reference || '').trim();
    if (!accessCode) {
      this.snackBar.open('Missing Paystack access code from top-up init', 'Close', { duration: 5000 });
      this.purchasingTopup = false;
      return;
    }

    const inlineLoaded = await this.ensurePaystackInlineLoaded();
    const PaystackCtor = this.getPaystackInlineConstructor();
    if (!inlineLoaded || !PaystackCtor) {
      window.location.assign(init.authorizationUrl);
      return;
    }

    const popup = new PaystackCtor();
    if (typeof popup.resumeTransaction !== 'function') {
      window.location.assign(init.authorizationUrl);
      return;
    }

    try {
      popup.resumeTransaction(accessCode, {
        onSuccess: (transaction) => {
          const confirmedReference = String(transaction?.reference || transaction?.trxref || reference).trim();
          if (!confirmedReference) {
            this.snackBar.open('Payment confirmed but reference is missing', 'Close', { duration: 5000 });
            this.purchasingTopup = false;
            return;
          }
          this.beginAwaitingPaymentConfirmation(confirmedReference, 'topup');
        },
        onCancel: () => {
          this.snackBar.open('Top-up purchase was cancelled.', 'Close', { duration: 4000 });
          this.purchasingTopup = false;
        },
        onError: (error) => {
          const message = String(error?.message || 'Paystack modal failed to load').trim();
          this.snackBar.open(message, 'Close', { duration: 6000 });
          this.purchasingTopup = false;
        },
      });
    } catch {
      window.location.assign(init.authorizationUrl);
    }
  }

  private getPaystackInlineConstructor(): PaystackInlineConstructor | undefined {
    const win = window as Window & { Paystack?: PaystackInlineConstructor; PaystackPop?: PaystackInlineConstructor };
    return win.PaystackPop || win.Paystack;
  }

  private async ensurePaystackInlineLoaded(): Promise<boolean> {
    if (this.getPaystackInlineConstructor()) return true;

    return new Promise<boolean>((resolve) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-paystack-inline-v2="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(Boolean(this.getPaystackInlineConstructor())), { once: true });
        existing.addEventListener('error', () => resolve(false), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v2/inline.js';
      script.async = true;
      script.defer = true;
      script.setAttribute('data-paystack-inline-v2', 'true');
      script.onload = () => resolve(Boolean(this.getPaystackInlineConstructor()));
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  private loadSelectorData(): void {
    this.loading = true;
    const scope = this.selectorScope;
    forkJoin({
      profile: this.apiService.getCurrentUserProfile(),
      plans: this.apiService.getBillingPlans(scope),
      billingProfile: scope === 'team' ? this.apiService.getBillingProfile() : of(null),
    }).subscribe({
      next: ({ profile, plans, billingProfile }) => {
        const mode = String(this.route.snapshot.queryParamMap.get('mode') || '').trim().toLowerCase();
        const allowPlanChange = mode === 'change' || mode === 'resubscribe';
        if (!this.shouldShowSelector(profile.user, allowPlanChange, scope)) {
          void this.navigateToDefault(profile.user);
          return;
        }
        if (scope === 'team') {
          this.plans = this.buildTeamScopeCards(plans, billingProfile);
          return;
        }
        const isResubscribe = mode === 'resubscribe';
        this.plans = (plans.plans || [])
          // Resubscribe is Standard-only — do not offer PAYG migration.
          .filter((plan) => !(isResubscribe && plan.planCode === 'INDIVIDUAL_PAYG'))
          .map((plan) => {
            const monthlyAmountCents = Number(plan.monthlyPriceCents || 0);
            return {
              ...plan,
              description: this.describePlan(plan.planCode, scope),
              displayMonthlyPriceCents: monthlyAmountCents,
              cardRequired: plan.planCode !== 'INDIVIDUAL_PAYG',
              highlights: this.planHighlights(plan),
            };
          })
          .sort((a, b) => {
            // Standard first, then PAYG
            if (a.planCode === 'INDIVIDUAL_STANDARD') return -1;
            if (b.planCode === 'INDIVIDUAL_STANDARD') return 1;
            return a.displayName.localeCompare(b.displayName);
          });
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to load billing plans', 'Close', {
          duration: 5000,
        });
        void this.router.navigate(['/user/data-request']);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private loadPackStep(): void {
    this.loading = true;
    this.loadingMessage = 'Loading credit packs...';
    forkJoin({
      profile: this.apiService.getCurrentUserProfile(),
      packs: this.apiService.getTopupPacks(),
    }).subscribe({
      next: ({ profile, packs }) => {
        if (profile.user.approvalStatus !== 'APPROVED' || profile.user.team === true) {
          void this.navigateToDefault(profile.user);
          return;
        }
        // Pack step only after PAYG is selected (selectedPlanId set).
        if (!profile.user.selectedPlanId) {
          this.step = 'plans';
          this.loadSelectorData();
          return;
        }
        this.topupPackOptions = packs.packs || [];
        if (!this.topupPackOptions.some((pack) => pack.packSize === this.selectedTopupPack)) {
          this.selectedTopupPack =
            this.topupPackOptions.find((p) => p.packSize === 100)?.packSize ||
            this.topupPackOptions[0]?.packSize ||
            50;
        }
      },
      error: (error) => {
        this.snackBar.open(error?.error?.error?.message || 'Failed to load credit packs', 'Close', {
          duration: 5000,
        });
        void this.navigateToDefaultAfterSetup();
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private shouldShowSelector(
    user: CurrentUserProfile,
    allowPlanChange: boolean,
    scope: 'individual' | 'team'
  ): boolean {
    if (user.approvalStatus !== 'APPROVED') return false;
    if (scope === 'team') {
      if (allowPlanChange) {
        return user.role === 'admin' && user.team === true && user.isBillingOwner === true;
      }
      return (
        user.role === 'admin' &&
        user.team === true &&
        user.isBillingOwner === true &&
        user.requiresTeamBillingSetup === true
      );
    }
    if (user.team === true) return false;
    // Individual plans are sticky after first chooser — only show for users without a plan.
    // Resubscribe for cancelled Standard still uses subscription init from My Account,
    // but do not reopen Standard ↔ PAYG chooser via mode=change.
    if (allowPlanChange && modeIsResubscribeOnly(this.route)) {
      return !user.selectedPlanId || String(user.selectedPlanCode || '') === 'INDIVIDUAL_STANDARD';
    }
    return !user.selectedPlanId;
  }

  private async navigateToDefault(user: CurrentUserProfile): Promise<void> {
    if (user.role === 'admin') {
      await this.router.navigate(['/admin/new-request']);
      return;
    }
    await this.router.navigate(['/user/data-request']);
  }

  private describePlan(planCode: string, scope: 'individual' | 'team'): string {
    if (scope === 'team') {
      return 'Team billing is configured and charged monthly with included credits per active seat.';
    }
    if (planCode === 'INDIVIDUAL_STANDARD') {
      return 'Monthly subscription with included credits. Card required to continue.';
    }
    if (planCode === 'INDIVIDUAL_PAYG') {
      return 'No monthly fee. Buy credit packs anytime.';
    }
    return 'Select this plan to continue.';
  }

  private planHighlights(plan: BillingPlansResponse['plans'][number]): string[] {
    if (plan.planCode === 'INDIVIDUAL_PAYG') {
      return ['No monthly fee', 'Buy credit packs anytime'];
    }
    if (plan.planCode === 'INDIVIDUAL_STANDARD') {
      return [
        `${plan.includedCreditsPerUser} included credits / month`,
        'Card required',
        `R${(Number(plan.topupCreditPriceCents || 0) / 100).toFixed(2)} per top-up credit`,
      ];
    }
    return [];
  }

  private buildTeamScopeCards(
    plansResponse: BillingPlansResponse,
    billingProfile: BillingProfileResponse | null
  ): IndividualPlanCard[] {
    const availablePlans = plansResponse.plans || [];
    if (availablePlans.length === 0) {
      return [];
    }
    const seatCount = Math.max(1, Number(billingProfile?.teamSeatCount || 1));
    const selectedPlan = this.selectTeamPlanForSeatCount(availablePlans, seatCount);
    const unitPriceCents = Math.max(
      0,
      Number(billingProfile?.teamUnitPriceCents || selectedPlan.monthlyPriceCents || 0)
    );
    return [
      {
        ...selectedPlan,
        description: this.describePlan(selectedPlan.planCode, 'team'),
        displayMonthlyPriceCents: unitPriceCents,
        cardRequired: true,
        highlights: [],
      },
    ];
  }

  private selectTeamPlanForSeatCount(
    plans: BillingPlansResponse['plans'],
    seatCount: number
  ): BillingPlansResponse['plans'][number] {
    const targetCode = seatCount >= 5 ? 'TEAM_GTE5' : 'TEAM_LT5';
    const exact = plans.find((plan) => plan.planCode === targetCode);
    if (exact) return exact;
    const byHint = plans.find((plan) =>
      seatCount >= 5 ? plan.planCode.includes('GTE5') : plan.planCode.includes('LT5')
    );
    return byHint || plans[0];
  }

  private resolveScopeFromRoute(): 'individual' | 'team' {
    const raw = String(this.route.snapshot.queryParamMap.get('scope') || '').trim().toLowerCase();
    return raw === 'team' ? 'team' : 'individual';
  }

  private beginAwaitingPaymentConfirmation(reference: string, kind: 'subscription' | 'topup'): void {
    this.awaitingPaymentConfirmation = true;
    this.pendingConfirmationTimedOut = false;
    this.activePendingReference = reference;
    this.awaitingPaymentMessage = 'Waiting for payment confirmation...';
    this.loading = false;
    this.selectingPlanCode = null;
    this.purchasingTopup = false;
    if (kind === 'topup') {
      this.pollTopupPurchaseStatus(reference, 1);
      return;
    }
    this.pollSubscriptionSetupStatus(reference, 1);
  }

  private endAwaitingPaymentConfirmation(): void {
    this.awaitingPaymentConfirmation = false;
    this.awaitingPaymentMessage = '';
    this.pendingConfirmationTimedOut = false;
    this.activePendingReference = null;
    this.loadingMessage = 'Loading available plans...';
  }

  onRetryPendingStatus(): void {
    const reference = String(this.activePendingReference || '').trim();
    if (!reference) {
      this.snackBar.open('Missing payment reference. Please contact support.', 'Close', { duration: 5000 });
      return;
    }
    this.pendingConfirmationTimedOut = false;
    this.awaitingPaymentMessage = 'Rechecking payment confirmation...';
    if (reference.startsWith('topup_')) {
      this.pollTopupPurchaseStatus(reference, 1);
      return;
    }
    this.pollSubscriptionSetupStatus(reference, 1);
  }

  onContactSupport(): void {
    const reference = String(this.activePendingReference || '').trim() || 'unknown';
    const userId = String(this.currentUserId || '').trim() || 'unknown';
    const subject = encodeURIComponent(
      `TBDS payment pending | user:${userId} | tx:${reference}`
    );
    const body = encodeURIComponent(
      [
        'Hello Support Team,',
        '',
        'My payment is still pending confirmation.',
        '',
        `User ID: ${userId}`,
        `Transaction Reference: ${reference}`,
        `Scope: ${this.selectorScope}`,
        `Current URL: ${window.location.href}`,
        `Reported At: ${new Date().toISOString()}`,
        '',
        'Please help me reconcile this payment.',
      ].join('\n')
    );
    window.location.href = `mailto:${encodeURIComponent(this.supportEmail)}?subject=${subject}&body=${body}`;
  }

  private async navigateToDefaultAfterSetup(): Promise<void> {
    if (this.selectorScope === 'team') {
      await this.router.navigate(['/admin/new-request']);
      return;
    }
    await this.router.navigate(['/user/data-request']);
  }

  private pollSubscriptionSetupStatus(reference: string, attempt: number): void {
    this.apiService.getSubscriptionSetupStatus(reference).subscribe({
      next: async (status) => {
        if (status.status === 'SUCCESS') {
          this.snackBar.open('Subscription activation successful', 'Close', {
            duration: 5000,
          });
          await this.navigateToDefaultAfterSetup();
          return;
        }
        if (status.status === 'FAILED') {
          this.snackBar.open(status.message || 'Payment failed', 'Close', { duration: 5000 });
          this.endAwaitingPaymentConfirmation();
          this.loadSelectorData();
          return;
        }
        if (attempt >= this.maxVerifyPollAttempts) {
          this.pendingConfirmationTimedOut = true;
          this.awaitingPaymentMessage = 'Payment is still pending confirmation.';
          this.snackBar.open('Payment is still pending confirmation. Please contact support.', 'Close', {
            duration: 6000,
          });
          return;
        }
        const remaining = this.maxVerifyPollAttempts - attempt;
        this.awaitingPaymentMessage = `Waiting for payment confirmation... retrying (${remaining} left)`;
        window.setTimeout(() => this.pollSubscriptionSetupStatus(reference, attempt + 1), this.verifyPollIntervalMs);
      },
      error: (error) => {
        if (this.isRetryableVerifyError(error) && attempt < this.maxVerifyPollAttempts) {
          const remaining = this.maxVerifyPollAttempts - attempt;
          this.awaitingPaymentMessage = `Waiting for payment confirmation... retrying (${remaining} left)`;
          window.setTimeout(() => this.pollSubscriptionSetupStatus(reference, attempt + 1), this.verifyPollIntervalMs);
          return;
        }
        this.snackBar.open(error?.error?.error?.message || 'Failed to confirm payment status', 'Close', {
          duration: 5000,
        });
        this.endAwaitingPaymentConfirmation();
        this.loadSelectorData();
      },
    });
  }

  private pollTopupPurchaseStatus(reference: string, attempt: number): void {
    this.apiService.getTopupPurchaseStatus(reference).subscribe({
      next: async (status) => {
        if (status.status === 'SUCCESS') {
          this.snackBar.open('Credits purchased successfully', 'Close', { duration: 5000 });
          this.purchasingTopup = false;
          await this.navigateToDefaultAfterSetup();
          return;
        }
        if (status.status === 'FAILED') {
          this.snackBar.open(status.message || 'Top-up payment failed', 'Close', { duration: 5000 });
          this.purchasingTopup = false;
          this.endAwaitingPaymentConfirmation();
          this.step = 'packs';
          this.loadPackStep();
          return;
        }
        if (attempt >= this.maxVerifyPollAttempts) {
          this.pendingConfirmationTimedOut = true;
          this.awaitingPaymentMessage = 'Payment is still pending confirmation.';
          this.purchasingTopup = false;
          return;
        }
        const remaining = this.maxVerifyPollAttempts - attempt;
        this.awaitingPaymentMessage = `Waiting for payment confirmation... retrying (${remaining} left)`;
        window.setTimeout(() => this.pollTopupPurchaseStatus(reference, attempt + 1), this.verifyPollIntervalMs);
      },
      error: (error) => {
        if (this.isRetryableVerifyError(error) && attempt < this.maxVerifyPollAttempts) {
          const remaining = this.maxVerifyPollAttempts - attempt;
          this.awaitingPaymentMessage = `Waiting for payment confirmation... retrying (${remaining} left)`;
          window.setTimeout(() => this.pollTopupPurchaseStatus(reference, attempt + 1), this.verifyPollIntervalMs);
          return;
        }
        // Fallback verify for race where status endpoint lags.
        this.apiService.verifyTopupPurchase(reference).subscribe({
          next: async () => {
            this.snackBar.open('Credits purchased successfully', 'Close', { duration: 5000 });
            this.purchasingTopup = false;
            await this.navigateToDefaultAfterSetup();
          },
          error: (verifyError) => {
            this.snackBar.open(
              verifyError?.error?.error?.message || error?.error?.error?.message || 'Failed to confirm top-up',
              'Close',
              { duration: 5000 }
            );
            this.purchasingTopup = false;
            this.endAwaitingPaymentConfirmation();
            this.step = 'packs';
            this.loadPackStep();
          },
        });
      },
    });
  }

  private isRetryableVerifyError(error: any): boolean {
    const code = String(error?.error?.error?.code || '').trim().toUpperCase();
    return code === 'NOT_FOUND' || code === 'INTERNAL_ERROR';
  }

  private loadCurrentUserContext(): void {
    this.apiService.getCurrentUserProfile().subscribe({
      next: (profile) => {
        this.currentUserId = String(profile?.user?.userId || '').trim() || null;
      },
      error: () => {
        this.currentUserId = null;
      },
    });
  }
}

function modeIsResubscribeOnly(route: ActivatedRoute): boolean {
  const mode = String(route.snapshot.queryParamMap.get('mode') || '').trim().toLowerCase();
  return mode === 'resubscribe';
}
